import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import useReferencingCategory from "$hooks/useReferencingCategory";
import { ProductService } from "$services";
import { CacheInvalidationAction } from "$utils/cache";
import { useDebounce } from "$utils/customHooks";
import SearchSelect from "./SearchSelect";

type Props = {
  onChange: (productId, product) => void;
  value: any;
  error?: boolean;
  multiple?: boolean;
  className?: any;
  required?: boolean;
  disabled?: boolean;
  disableClearable?: boolean;
  getProductLabel?: (opt) => string;
  companyId?: string;
  allowNoCompany?: boolean;
  createLabel?: string;
  label?: string;
  placeholder?: string;
  queryFieldForInit?: string;
  valueKey?: string;
  minCharsToTrigerSearch?: number;
  filterProductIds?: string[];
  rowsPerPage?: number;
  isActive?: boolean;
  searchByReference?: boolean;
};

const defaultGetProductLabel = (option) =>
  `${option?.designation}${option?.reference && ` - [${option?.reference}]`}`;

const ProductSelect = ({
  companyId,
  valueKey = "_id",
  filterProductIds = [],
  multiple,
  value,
  rowsPerPage = 20,
  onChange,
  getProductLabel = defaultGetProductLabel,
  disableClearable,
  createLabel,
  queryFieldForInit,
  minCharsToTrigerSearch = 2,
  isActive,
  searchByReference,
  ...props
}: Props) => {
  const { referencingCategory } = useReferencingCategory(companyId);
  const queryClient = useQueryClient();

  const [inputValue, setInputValue] = useState<string>("");
  const debouncedInputValue = useDebounce(inputValue);
  const [localOptions, setLocalOptions] = useState([]);

  // Track if we're updating from the parent component
  const [internalUpdate, setInternalUpdate] = useState(false);

  // If value changes externally (from parent), update the input value to match
  // This is similar to ProjectSelect's behavior, but also checks cache if not in localOptions
  useEffect(() => {
    // Ensure localOptions is always treated as an array
    const safeLocalOptions = Array.isArray(localOptions) ? localOptions : [];

    if (!internalUpdate) {
      if (value && safeLocalOptions.length > 0) {
        const selectedOption = multiple
          ? safeLocalOptions.filter((opt) =>
              (Array.isArray(value) ? value : [value]).includes(opt[valueKey])
            )
          : safeLocalOptions.find((opt) => opt[valueKey] === value);

        if (selectedOption && !multiple) {
          setInputValue(getProductLabel(selectedOption));
        } else if (multiple && selectedOption.length > 0) {
          setInputValue("");
        }
      } else if (value && queryFieldForInit) {
        // Value exists but not in localOptions, try to find it in cache
        // This handles the case when value is set programmatically
        const currentValue = multiple
          ? Array.isArray(value)
            ? value
            : [value]
          : [value];

        if (currentValue.length > 0) {
          const initValue = async () => {
            try {
              const idFilter = currentValue;
              const queryParams = {
                [queryFieldForInit]: idFilter,
              };

              // Try to find in cache first (same logic as SearchSelect's init)
              const sortedIdFilter = Array.isArray(idFilter)
                ? [...idFilter].sort()
                : idFilter;
              const normalizedQueryParams = {
                [queryFieldForInit]: sortedIdFilter,
              };
              const cacheKey = [
                CacheInvalidationAction.GET_PRODUCTS,
                normalizedQueryParams,
              ];
              let cachedData = queryClient.getQueryData(cacheKey);

              // If not in cache, fetch it
              if (!cachedData) {
                cachedData = await ProductService.getProductByReference(
                  queryParams
                );
                queryClient.setQueryData(cacheKey, cachedData);
              }

              // Extract products array
              let productsArray: any[] = [];
              if (Array.isArray(cachedData)) {
                productsArray = cachedData;
              } else if (
                cachedData &&
                typeof cachedData === "object" &&
                "data" in cachedData &&
                Array.isArray((cachedData as any).data)
              ) {
                productsArray = (cachedData as any).data;
              }

              if (productsArray.length > 0) {
                // Update localOptions with the found products
                setLocalOptions(productsArray);

                // Update inputValue
                if (!multiple && productsArray[0]) {
                  setInputValue(getProductLabel(productsArray[0]));
                }
              }
            } catch (error) {
              // Silently fail - the product might not be available yet
              console.error("Failed to fetch product for display:", error);
            }
          };

          initValue();
        }
      } else {
        // Value is empty/null, clear inputValue to allow searching
        setInputValue("");
      }
    }
  }, [
    value,
    localOptions,
    multiple,
    valueKey,
    internalUpdate,
    getProductLabel,
    queryFieldForInit,
    queryClient,
  ]);

  // Separate effect to handle clearing when value becomes empty (similar to ProjectSelect)
  useEffect(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setInputValue("");
    }
  }, [companyId, value]);

  const filters = useMemo(() => {
    const nextFilters = {
      ...(companyId && { company: companyId }),
      ...(!!debouncedInputValue && {
        [searchByReference ? "completeReference" : "textSearch"]:
          debouncedInputValue,
      }),
      referencingCategory: referencingCategory?.id,
      isReferenced: true,
      limit: rowsPerPage,
      page: 1,
      ...(isActive !== undefined && { isActive }),
    } as any;

    let isSearchStringEqualsAnOption = false;
    if (multiple && Array.isArray(value)) {
      isSearchStringEqualsAnOption = (value || []).some((val) => {
        const opt = localOptions.find((opt) => opt[valueKey] === val);
        return getProductLabel(opt) === debouncedInputValue;
      });
    } else {
      const foundOpt = localOptions.find((opt) => opt[valueKey] === value);
      isSearchStringEqualsAnOption =
        getProductLabel(foundOpt) === debouncedInputValue;
    }

    if (!multiple && !!debouncedInputValue && isSearchStringEqualsAnOption) {
      if (searchByReference) {
        nextFilters.completeReference = undefined;
      } else {
        nextFilters.textSearch = undefined;
      }
    }

    return nextFilters;
  }, [
    companyId,
    debouncedInputValue,
    referencingCategory?.id,
    rowsPerPage,
    multiple,
    value,
    localOptions,
    getProductLabel,
    valueKey,
    isActive,
    searchByReference,
  ]);

  // Custom onChange handler that manages the internal state
  const handleChange = (selectedValue: any, selectedOption: any) => {
    setInternalUpdate(true);

    try {
      // Update local display options
      if (selectedOption) {
        if (multiple) {
          setInputValue(""); // reset search on select
          setLocalOptions(
            Array.isArray(selectedOption) ? selectedOption : [selectedOption]
          );
        } else {
          setInputValue(getProductLabel(selectedOption));
          setLocalOptions([selectedOption]);
        }
      } else {
        setLocalOptions([]);
        setInputValue("");
      }

      // Pass to parent component
      if (onChange) {
        onChange(selectedValue, selectedOption);
      }
    } finally {
      // Reset the internal update flag after the cycle completes
      setTimeout(() => {
        setInternalUpdate(false);
      }, 0);
    }
  };

  return (
    <SearchSelect
      {...props}
      multiple={multiple}
      disableClearable={disableClearable}
      debouncedInputValue={debouncedInputValue}
      inputValue={inputValue}
      setInputValue={setInputValue}
      filterIds={filterProductIds}
      localOptions={localOptions}
      createLabel={createLabel}
      setLocalOptions={setLocalOptions}
      value={value}
      queryFieldForInit={queryFieldForInit}
      onChange={handleChange}
      valueKey={valueKey}
      noOptionHint="Aucun produit ne correspond"
      queryKey={[CacheInvalidationAction.GET_PRODUCTS, filters]}
      queryFn={ProductService.getProductByReference}
      labelKey="designation"
      labelKey2="reference"
      queryfilters={filters}
      enabled={filters !== null}
      minCharsToTrigerSearch={minCharsToTrigerSearch}
      rowsPerPage={rowsPerPage}
      shouldResetValueOnBlur={false}
      getOptionLabel={getProductLabel}
    />
  );
};

export default ProductSelect;
